/*
  Published Judge.me reviews for the reviews page and the homepage wall.

  Reads Judge.me's public widget endpoint (the one its All Reviews widget uses): no API
  token, open CORS, and only data the storefront already shows. It is undocumented, so
  the sections' own review blocks (if any) stay as the fallback when this returns nothing.
*/
(() => {
  if (window.CCKJudgeme) return;

  const ENDPOINT = 'https://cdn.judge.me/reviews/all_reviews_js_based';
  const PER_PAGE = 25; // fixed by Judge.me, per_page is ignored
  const CACHE_MS = 10 * 60 * 1000;

  const cacheRead = (key) => {
    try {
      const hit = JSON.parse(sessionStorage.getItem(key));
      return hit && Date.now() - hit.at < CACHE_MS ? hit.data : null;
    } catch (e) {
      return null;
    }
  };
  const cacheWrite = (key, data) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
    } catch (e) {
      /* storage full or blocked: just skip caching */
    }
  };

  const text = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');

  // Keeps paragraph and line breaks; the caller renders with white-space: pre-line.
  const bodyText = (node) => {
    if (!node) return '';
    node.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    const paragraphs = [...node.querySelectorAll('p')];
    const parts = paragraphs.length ? paragraphs.map((p) => p.textContent) : [node.textContent];
    return parts
      .map((part) => part.replace(/[ \t]+/g, ' ').trim())
      .filter(Boolean)
      .join('\n\n');
  };

  // DOMParser builds an inert document: nothing in the review HTML runs.
  const parse = (html) =>
    [...new DOMParser().parseFromString(html || '', 'text/html').querySelectorAll('.jdgm-rev')]
      .map((el) => {
        const link = el.querySelector('.jdgm-rev__prod-link');
        const handle = (link?.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1] || '';
        // "2026-10-08 20:23:52 UTC" isn't parseable everywhere (Safari); make it ISO first.
        const stamp = el.querySelector('.jdgm-rev__timestamp')?.dataset.content || '';
        return {
          id: el.dataset.reviewId,
          rating: Number(el.querySelector('.jdgm-rev__rating')?.dataset.score) || 0,
          author: text(el.querySelector('.jdgm-rev__author')),
          body: bodyText(el.querySelector('.jdgm-rev__body')),
          date: Date.parse(stamp.replace(' UTC', 'Z').replace(' ', 'T')) || 0,
          verified: el.dataset.verifiedBuyer === 'true',
          product: { handle, title: text(link) },
        };
      })
      .filter((review) => review.id && review.body && review.author);

  // Judge.me keeps product and store reviews apart; the page and the wall show both as one set.
  const TYPES = { 'product-reviews': 'number_of_product_reviews', 'shop-reviews': 'number_of_shop_reviews' };

  async function fetchPage(shop, type, rating, page) {
    const key = `cck-jdgm:${shop}:${type}:${rating}:${page}`;
    const cached = cacheRead(key);
    if (cached) return cached;
    const params = new URLSearchParams({
      url: shop,
      shop_domain: shop,
      platform: 'shopify',
      review_type: type,
      filter_rating: rating,
      page,
    });
    const response = await fetch(`${ENDPOINT}?${params}`);
    if (!response.ok) throw new Error(`Judge.me ${response.status}`);
    const json = await response.json();
    const data = { total: Number(json[TYPES[type]]) || 0, reviews: parse(json.html) };
    cacheWrite(key, data);
    return data;
  }

  // Every review of one type with exactly this star rating, newest first. limit caps the pages fetched.
  async function fetchRating(shop, type, rating, limit = Infinity) {
    const first = await fetchPage(shop, type, rating, 1);
    const pages = Math.min(Math.ceil(first.total / PER_PAGE), Math.ceil(limit / PER_PAGE));
    const rest = await Promise.all(
      Array.from({ length: Math.max(pages - 1, 0) }, (_, i) => fetchPage(shop, type, rating, i + 2).catch(() => null))
    );
    return [first, ...rest].flatMap((page) => (page ? page.reviews : [])).slice(0, limit);
  }

  // Product and store reviews rated minRating..5, newest first.
  async function loadReviews(shop, { minRating = 4, limit = Infinity } = {}) {
    const ratings = [5, 4, 3, 2, 1].filter((r) => r >= minRating);
    const lists = await Promise.all(
      Object.keys(TYPES).flatMap((type) => ratings.map((r) => fetchRating(shop, type, r, limit).catch(() => [])))
    );
    return lists
      .flat()
      .sort((a, b) => b.date - a.date)
      .slice(0, limit);
  }

  // Product + store count per star from one small request each: { average, total, counts: {5: n, …} }.
  async function loadSummary(shop) {
    const stars = [1, 2, 3, 4, 5];
    const totals = await Promise.all(
      stars.map((r) =>
        Promise.all(Object.keys(TYPES).map((type) => fetchPage(shop, type, r, 1).then((p) => p.total))).then((n) => n.reduce((a, b) => a + b, 0))
      )
    );
    const counts = Object.fromEntries(totals.map((n, i) => [stars[i], n]));
    const total = totals.reduce((sum, n) => sum + n, 0);
    const average = total ? totals.reduce((sum, n, i) => sum + n * stars[i], 0) / total : 0;
    return { average, total, counts };
  }

  // Fills a clone of <template data-review-card> (snippets/review-card.liquid) with textContent only.
  // href, when set, replaces the product page as the product link's destination.
  function renderCard(template, review, { products = {}, tags = [], via = '', verified = '', href = '', hidden = false } = {}) {
    const card = template.content.firstElementChild.cloneNode(true);
    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    card.dataset.tags = `,${tags.join(',')},`;
    card.dataset.source = 'judgeme';
    if (hidden) {
      card.setAttribute('aria-hidden', 'true');
      card.inert = true;
    }
    const stars = field('stars');
    if (stars) {
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      stars.setAttribute('aria-label', `${review.rating} / 5`);
    }
    field('quote').textContent = review.body;
    field('avatar').textContent = review.author.charAt(0).toUpperCase();
    field('name').textContent = review.author;
    field('source').textContent = review.verified && verified ? verified : `${via} Judge.me`.trim();

    const link = field('product');
    const product = products[review.product.handle];
    if (!review.product.handle) {
      link.remove();
    } else {
      link.href = href || product?.url || `/products/${review.product.handle}`;
      field('product-title').textContent = product?.title || review.product.title;
      const image = field('product-image');
      if (product?.image) image.src = product.image;
      else image.remove();
      if (hidden) link.tabIndex = -1;
    }
    return card;
  }

  window.CCKJudgeme = { loadReviews, loadSummary, renderCard };
})();
